import { createFileRoute } from "@tanstack/react-router";
import heroBg from "@/assets/heroes/colorado-ppc-hero.jpg";
import { Target } from "lucide-react";
import { StatewideServiceHub } from "@/components/StatewideServiceHub";
import { buildCityCards } from "@/lib/statewideServiceCities";
import { ogImageFor, ogMetaTags, twitterTextTags } from "@/lib/ogImages";
import { buildServiceHubScripts } from "@/lib/serviceHubSchema";

const CANONICAL = "https://coloradowebimpressions.com/google-ads-ppc";
const OG_IMAGE = `https://coloradowebimpressions.com${ogImageFor({ service: "ppc" })}`;

export const Route = createFileRoute("/google-ads-ppc")({
  head: () => ({
    meta: [
      { title: "Colorado Google Ads Management — No Ad-Spend Markup (2026)" },
      {
        name: "description",
        content:
          "In-house Colorado Google Ads management. Zero markup on ad spend, conversion landing pages, real call tracking. Month-to-month, owner-led since 2006.",
      },
      { property: "og:title", content: "Colorado Google Ads Management — No Ad-Spend Markup (2026)" },
      { property: "og:description", content: "Profitable PPC for Colorado cities. No markup on ad spend, month-to-month, owner-led." },
      { property: "og:url", content: CANONICAL },
      ...ogMetaTags(OG_IMAGE),
      ...twitterTextTags({
        title: "Colorado Google Ads — No Ad-Spend Markup",
        description: "Profitable paid search managed for your Colorado city.",
      }),
    ],
    links: [{ rel: "canonical", href: CANONICAL }],
    scripts: buildServiceHubScripts({
      path: "/google-ads-ppc",
      name: "Google Ads & PPC",
      description: "In-house Google Ads and PPC management for Colorado businesses with no markup on ad spend, conversion-tested landing pages, and city-level targeting.",
      serviceType: "PPC Management",
      priceRange: "$$",
      faqs: [
        { q: "How much should I budget for Google Ads in Colorado?", a: "Most Colorado service businesses see strong results with a $1,500–$5,000/month ad spend plus a $600–$1,200/month management fee. We never mark up ad spend — every dollar you budget goes to Google." },
        { q: "Do you require a long-term PPC contract?", a: "No. Management is month-to-month with no cancellation fees. You always own the Google Ads account, the conversion data, and the landing pages." },
        { q: "Do you build landing pages or use my homepage?", a: "We build conversion-tested landing pages for every campaign. Sending paid traffic to a generic homepage cuts conversion rates by 30–60% on most accounts." },
        { q: "Will my ads only show in Colorado?", a: "Yes — every campaign is geo-targeted to the cities and ZIP codes you actually serve. We exclude tire-kicker regions to keep cost-per-lead down." },
      ],
    }),
  }),
  component: () => (
    <StatewideServiceHub
      heroImage={{ src: heroBg, alt: "Colorado Google Ads PPC campaign dashboards with Denver skyline — Colorado Web Impressions" }}
      service="Google Ads"
      serviceLower="Google Ads / PPC"
      eyebrow="Colorado PPC"
      headline="Google Ads built for Colorado markets — pick your city"
      goldWord="built"
      subheadline="In-house PPC management with no markup on ad spend. Conversion-tested landing pages, real call tracking, and city-specific targeting that actually books work."
      benefits={[
        "No markup on ad spend — what you spend is what runs",
        "Conversion-tested landing pages, not just ad management",
        "Real call tracking + lead attribution",
        "City + neighborhood-level geo-targeting",
        "Month-to-month — no long PPC contracts",
      ]}
      benefitIcon={Target}
      servicesGrid
      industriesGrid
      cities={buildCityCards("ppc", "/colorado-springs-ppc")}
      pickerHeading="Pick your Colorado city for Google Ads"
      closing="Running ads in a city we haven't built a page for yet? We still take on Google Ads accounts for any Colorado market — contact us to scope a campaign."
    />
  ),
});
